/**
 * Nearest point calculation for circle entities.
 */

import type { DrawingModule } from "../../types/DrawingWeb";
import type { DrawingModuleType } from "../../DrawingSingleton";
import { deleteObj } from "../../core/delete";

/**
 * Get the closest point on a circle to a given point.
 * Uses simple math to project the point onto the circle perimeter.
 *
 * @param module The ODA drawing module
 * @param circle The circle entity
 * @param givenPoint The point to find the closest point to
 * @returns The closest point on the circle
 */
export function getClosestPointToCircle(
  module: DrawingModuleType,
  circle: DrawingModule.OdDbCircle,
  givenPoint: DrawingModule.OdGePoint3d,
): DrawingModule.OdGePoint3d {
  const center = circle.center();
  const radius = circle.radius();

  const dx = givenPoint.x - center.x;
  const dy = givenPoint.y - center.y;
  const dist = Math.sqrt(dx * dx + dy * dy);

  let result: DrawingModule.OdGePoint3d;
  if (dist < 1e-10) {
    // Point is at center, return arbitrary point on circle
    result = new module.OdGePoint3d(center.x + radius, center.y, center.z);
  } else {
    // Normalize direction and scale by radius
    const scale = radius / dist;
    result = new module.OdGePoint3d(
      center.x + dx * scale,
      center.y + dy * scale,
      center.z,
    );
  }

  deleteObj(center);
  return result;
}
